import { type AgentRecord } from "@/shared/domain/agents";
import {
  type AdminUpdateFeedbackRequest,
  type AgentFeedbackRecord,
  type AgentThreadRecord,
  FeedbackCategory,
  FeedbackSentiment,
} from "@/shared/domain/agentThread";
import { type UserRecord } from "@/shared/domain/users";
import { HttpClient, PaginatedResponse } from "@/shared/http";
import type { SearchQueryBody } from "@/types";

import { ReconstructedPrompt, SessionDebugSummary } from "./ChatDebugRecord";
import { MigrationRecord, PerActionStats } from "./MigrationRecord";

/**
 * One row of the admin user search: identifier plus display label. The backend
 * projects only these two columns, so no other `UserRecord` field is on the wire.
 */
export type UserSearchResult = Pick<UserRecord, "user_id" | "name">;

/**
 * One row of the admin agent search: identifier, display name, and owning
 * org. The backend projects only these three columns, so no other
 * `AgentRecord` field (``request_template``, ``variables``, timestamps, ...)
 * is on the wire.
 */
export type AgentSearchResult = Pick<
  AgentRecord,
  "agent_id" | "name" | "org_id"
>;

interface Options {
  abortSignal?: AbortSignal;
}

interface GetPerActionStatsOptions extends Options {
  startTime: number; // Unix timestamp in seconds
  endTime: number; // Unix timestamp in seconds
  limit?: number;
}

interface GetAdminAgentThreadsOptions extends Options {
  orgId?: string;
  createdBy?: string;
  sessionId?: string;
  hasErrors?: boolean;
  pageToken?: string;
  limit?: number;
}

/**
 * Filters for ``GET v1/admin/feedback``. Each array field keeps rows matching
 * any of its values (one query param per value); an empty or missing array
 * leaves the field unfiltered. Fields combine with AND.
 */
export interface GetAdminFeedbackOptions extends Options {
  sentiment?: FeedbackSentiment[];
  /** Keeps rows whose categories include at least one of these. */
  category?: FeedbackCategory[];
  orgId?: string[];
  sessionId?: string;
  createdBy?: string[];
  resolved?: boolean;
  hasNotes?: boolean;
  hasErrors?: boolean;
  /**
   * Customer-only feedback filter (the admin "Customer only" control). ``true``
   * excludes Roboto staff (`@roboto.ai`) and service accounts (`.srv`) by the
   * feedback author; ``false`` keeps only those. Mirrors the threads search
   * ``is_customer`` pseudo-field.
   */
  isCustomer?: boolean;
  /** Keeps rows whose ``admin_label`` equals one of these exactly (case-sensitive). */
  adminLabel?: string[];
  createdAfter?: string; // ISO datetime
  createdBefore?: string; // ISO datetime
  pageToken?: string;
  limit?: number;
}

/** One admin triage label and how many feedback rows carry it. */
/** How many labels ``GET v1/admin/feedback-labels`` returns at most. */
export const ADMIN_FEEDBACK_LABEL_LIMIT = 200;

export interface AdminFeedbackLabelCount {
  label: string;
  count: number;
}

export interface FeedbackCountsBySession {
  [sessionId: string]: { positive: number; negative: number };
}

/**
 * Backfill the canonical ``thread_id`` / ``forked_from_thread_id`` fields
 * from their legacy ``session_id`` / ``forked_from_session_id`` siblings
 * when the wire response carries the pre-v2026_05_20 shape. Lossless on
 * already-canonical payloads.
 *
 * Defensive on the read side because admin /chats has been observed
 * returning the legacy shape post-deploy even though its route does not
 * register the ``AgentThreadRenameTransform``. Until that backend path is
 * pinned down, normalizing here keeps the table navigable.
 */
function normalizeThreadShape(record: AgentThreadRecord): AgentThreadRecord {
  const legacy = record as AgentThreadRecord & {
    session_id?: string;
    forked_from_session_id?: string | null;
  };
  if (record.thread_id !== undefined && record.thread_id !== null) {
    return record;
  }
  if (legacy.session_id === undefined) {
    return record;
  }
  return {
    ...record,
    thread_id: legacy.session_id,
    forked_from_thread_id:
      record.forked_from_thread_id ?? legacy.forked_from_session_id ?? null,
  };
}

export class AdminService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getMigrations(
    options?: Partial<Options>,
  ): Promise<MigrationRecord[]> {
    const requestUrl = this.#httpClient.constructUrl("v1/admin/migrations");

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return await response.json<MigrationRecord[]>();
  }

  public async getPerActionStats(
    options: GetPerActionStatsOptions,
  ): Promise<PerActionStats[]> {
    const searchParams = new URLSearchParams();
    searchParams.append("start_time", options.startTime.toString());
    searchParams.append("end_time", options.endTime.toString());

    if (options.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/per-action-stats",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return await response.json<PerActionStats[]>();
  }

  public async getAdminAgentThreads(
    options?: GetAdminAgentThreadsOptions,
  ): Promise<PaginatedResponse<AgentThreadRecord>> {
    const searchParams = new URLSearchParams();

    if (options?.orgId) {
      searchParams.append("org_id", options.orgId);
    }
    if (options?.createdBy) {
      searchParams.append("created_by", options.createdBy);
    }
    if (options?.sessionId) {
      searchParams.append("thread_id", options.sessionId);
    }
    if (options?.hasErrors !== undefined) {
      searchParams.append("has_errors", options.hasErrors.toString());
    }
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options?.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/chats",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    // The wire response can arrive shaped with the legacy ``session_id``
    // (and ``forked_from_session_id``) fields when the API version transform
    // fires — observed on deployed lambdas serving admin /chats even though
    // the route doesn't register the transform locally. Normalize before
    // handing the rows to React: downstream code only knows ``thread_id``.
    const raw = await response.json<PaginatedResponse<AgentThreadRecord>>();
    return {
      ...raw,
      items: raw.items.map(normalizeThreadShape),
    };
  }

  /**
   * Structured search over every agent thread, for the admin thread explorer.
   *
   * POSTs a ``SearchQueryBody`` (structured ``condition`` + sort + ``after``
   * token) to ``v1/admin/threads/search`` — the structured sibling of
   * ``getAdminAgentThreads``. The backend (``ROBOTO_ADMIN_ONLY``) enforces a
   * filter whitelist (the public thread-search fields plus ``org_id`` and the
   * boolean pseudo-fields ``has_errors`` / ``is_customer``) and a sort
   * whitelist (``created``); an out-of-whitelist field returns 400 and
   * surfaces as a rejected promise.
   *
   * No org header: the endpoint spans every org and is gated purely on admin
   * identity. Responses are normalized through {@link normalizeThreadShape}
   * for the same legacy-``session_id`` reason as ``getAdminAgentThreads``.
   */
  public async searchAdminAgentThreads(
    query: SearchQueryBody,
    options?: Options,
  ): Promise<PaginatedResponse<AgentThreadRecord>> {
    const requestUrl = this.#httpClient.constructUrl("v1/admin/threads/search");

    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(query),
    });

    const raw = await response.json<PaginatedResponse<AgentThreadRecord>>();
    return {
      ...raw,
      items: raw.items.map(normalizeThreadShape),
    };
  }

  /**
   * Structured server-side search over every user, for the admin thread
   * explorer's User filter. POSTs a ``SearchQueryBody`` (structured
   * ``condition`` + sort + ``after`` token) to ``v1/admin/users/search`` —
   * the same contract as ``searchAdminAgentThreads``. The backend enforces a
   * filter whitelist (``user_id``, ``name``) and a sort whitelist
   * (``user_id``, ``name``; default ``user_id`` ascending); an
   * out-of-whitelist field returns 400 and surfaces as a rejected promise.
   *
   * Backs the filter widget's lazy option resolver: the widget calls it only
   * when the User picker opens (and again, debounced, as the admin types), so
   * nothing is fetched at page mount. Cross-org and gated purely on admin
   * identity — there is no org header. Callers cap results via ``limit``
   * (the thread-explorer filters request 100); ``next_token`` is non-null
   * when the backend truncated the result set.
   *
   * Results carry only the searched columns (`user_id`, `name`) — the backend
   * deliberately does not return full `UserRecord`s, so fields like
   * `is_service_user` are not available here.
   */
  public async searchAdminUsers(
    query: SearchQueryBody,
    options?: Options,
  ): Promise<PaginatedResponse<UserSearchResult>> {
    const requestUrl = this.#httpClient.constructUrl("v1/admin/users/search");

    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(query),
    });

    return response.json<PaginatedResponse<UserSearchResult>>();
  }

  /**
   * Structured server-side search over every agent, for the admin thread
   * explorer. POSTs a ``SearchQueryBody`` to ``v1/admin/agents/search`` —
   * the same contract as ``searchAdminAgentThreads``. The backend enforces a
   * filter whitelist (``agent_id``, ``name``, ``org_id``) and a sort
   * whitelist (``created``, ``agent_id``, ``name``; default ``created``
   * descending); an out-of-whitelist field returns 400 and surfaces as a
   * rejected promise.
   *
   * Serves two callers:
   *  - the Agent filter's lazy option resolver (a ``CONTAINS`` condition on
   *    ``name``), fired only when the picker opens; and
   *  - the per-page Agent-name column resolution (an OR group of EQUALS
   *    conditions on ``agent_id``), which batches the distinct
   *    ``created_from_agent_id`` values not already cached from an earlier
   *    page into one call (see ``useAdminAgentsByIds``).
   *
   * Cross-org and gated purely on admin identity — no org header.
   *
   * Results carry only the searched columns (`agent_id`, `name`, `org_id`) —
   * the backend deliberately does not return full `AgentRecord`s, so fields
   * like `request_template` are not available here.
   */
  public async searchAdminAgents(
    query: SearchQueryBody,
    options?: Options,
  ): Promise<PaginatedResponse<AgentSearchResult>> {
    const requestUrl = this.#httpClient.constructUrl("v1/admin/agents/search");

    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(query),
    });

    return response.json<PaginatedResponse<AgentSearchResult>>();
  }

  public async getAdminFeedback(
    options?: GetAdminFeedbackOptions,
  ): Promise<PaginatedResponse<AgentFeedbackRecord>> {
    const searchParams = new URLSearchParams();
    const appendEach = (name: string, values: string[] | undefined) => {
      for (const value of values ?? []) {
        searchParams.append(name, value);
      }
    };

    appendEach("sentiment", options?.sentiment);
    appendEach("category", options?.category);
    appendEach("org_id", options?.orgId);
    if (options?.sessionId) {
      searchParams.append("thread_id", options.sessionId);
    }
    appendEach("created_by", options?.createdBy);
    if (options?.resolved !== undefined) {
      searchParams.append("resolved", options.resolved.toString());
    }
    if (options?.hasNotes !== undefined) {
      searchParams.append("has_notes", options.hasNotes.toString());
    }
    if (options?.hasErrors !== undefined) {
      searchParams.append("has_errors", options.hasErrors.toString());
    }
    if (options?.isCustomer !== undefined) {
      searchParams.append("is_customer", options.isCustomer.toString());
    }
    appendEach("admin_label", options?.adminLabel);
    if (options?.createdAfter) {
      searchParams.append("created_after", options.createdAfter);
    }
    if (options?.createdBefore) {
      searchParams.append("created_before", options.createdBefore);
    }
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options?.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/feedback",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.json<PaginatedResponse<AgentFeedbackRecord>>();
  }

  public async updateAdminFeedback(
    feedbackId: string,
    request: AdminUpdateFeedbackRequest,
    options?: Options,
  ): Promise<AgentFeedbackRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/feedback/${feedbackId}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    return response.json<AgentFeedbackRecord>();
  }

  /**
   * Non-empty admin triage labels in use, most-used first, at most
   * {@link ADMIN_FEEDBACK_LABEL_LIMIT}. ``contains`` narrows them to labels
   * containing that text (case-insensitive), so a rarely used label past the
   * cap can still be found.
   */
  public async getAdminFeedbackLabels(
    options?: Options & { contains?: string },
  ): Promise<AdminFeedbackLabelCount[]> {
    const searchParams = new URLSearchParams();
    if (options?.contains) {
      searchParams.append("contains", options.contains);
    }
    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/feedback-labels",
      searchParams,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<AdminFeedbackLabelCount[]>();
  }

  public async getAdminFeedbackById(
    feedbackId: string,
    options?: Options,
  ): Promise<AgentFeedbackRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/feedback/${feedbackId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<AgentFeedbackRecord>();
  }

  public async getChatDebugSummary(
    chatId: string,
    options?: Options,
  ): Promise<SessionDebugSummary> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/chats/${chatId}/debug`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<SessionDebugSummary>();
  }

  public async getChatMessagePrompt(
    chatId: string,
    messageSequenceNum: number,
    options?: Options,
  ): Promise<ReconstructedPrompt> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/chats/${chatId}/messages/${messageSequenceNum}/prompt`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<ReconstructedPrompt>();
  }

  public async getAdminFeedbackCountsForAgentThreads(
    filters: {
      orgId?: string;
      createdBy?: string;
      sessionId?: string;
    },
    options?: Options,
  ): Promise<FeedbackCountsBySession> {
    // Backend rejects unrestricted scans (no filter) with a 400. Throw here
    // rather than silently returning ``{}``: an empty-object response would
    // mask both an honest "no rows match" and the misuse-of-the-API case
    // where the caller forgot to gate this call. Hook callers
    // (``useAdminFeedbackCountsForAgentThreads``) already pin ``enabled`` on the
    // same predicate, so this throw doesn't fire on the happy path.
    if (!filters.orgId && !filters.createdBy && !filters.sessionId) {
      throw new Error(
        "getAdminFeedbackCountsForAgentThreads requires at least one of orgId, createdBy, or sessionId.",
      );
    }

    const searchParams = new URLSearchParams();
    if (filters.orgId) {
      searchParams.append("org_id", filters.orgId);
    }
    if (filters.createdBy) {
      searchParams.append("created_by", filters.createdBy);
    }
    if (filters.sessionId) {
      searchParams.append("thread_id", filters.sessionId);
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/chats/feedback-counts",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.json<FeedbackCountsBySession>();
  }
}
